import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { Network } from "@capacitor/network";
import { Preferences } from "@capacitor/preferences";
import { PushNotifications } from "@capacitor/push-notifications";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiClient } from "./api/ApiClient";
import {
  ApiError,
  AuthenticationRequiredError,
  NetworkError,
  ResponseValidationError,
} from "./api/errors";
import { App } from "./app/App";
import type { AppServices } from "./app/App";
import { readAppConfig } from "./app/config";
import { createUuidV7Generator } from "./domain/ids";
import { DexieLocalStore, TimeTrackingDatabase } from "./offline/DexieLocalStore";
import type { OfflineServices } from "./offline/OfflineContext";
import type { SyncController } from "./offline/SyncContext";
import { SyncEngine } from "./offline/SyncEngine";
import { createHttpSyncApi } from "./offline/SyncApi";
import { isSyncRequest, startBackgroundSyncSupport, tokenExpiryMs } from "./offline/backgroundSync";
import type { SyncRegistrar } from "./offline/backgroundSync";
import { settingKeys } from "./offline/LocalStore";
import { startSyncTriggers } from "./offline/syncTriggers";
import { createNativeUserManager } from "./platform/nativeAuth";
import { createHttpNativePushApi, NativePushNotificationController } from "./platform/nativePush";
import { NativePlatformServices } from "./platform/NativePlatformServices";
import type {
  AppBadgePluginPort,
  GeofenceReminderPluginPort,
  NfcPluginPort,
} from "./platform/NativePlatformServices";
import type { PlatformServices } from "./platform/PlatformServices";
import { WebPlatformServices } from "./platform/WebPlatformServices";
import {
  createInactiveUpdateController,
  serviceWorkerScriptUrl,
  ServiceWorkerUpdater,
  startUpdateChecks,
} from "./pwa/AppUpdater";
import { BrowserInstallController } from "./pwa/installPrompt";
import { detectPlatform, pushRequiresInstallation } from "./pwa/platform";
import {
  createHttpPushApi,
  createUnsupportedPushController,
  WebPushNotificationController,
} from "./pwa/pushNotifications";
import type { PushNotificationController } from "./pwa/pushNotifications";
import { createBrowserUserManager, OidcAuthService } from "./shared/auth/OidcAuthService";
import { ClientLogger } from "./shared/logging/clientLog";
import type { ClientLogDelivery } from "./shared/logging/clientLog";
import { deliverClientLogs } from "./shared/logging/deliverClientLogs";
import { reportGlobalErrors } from "./shared/logging/globalErrors";
import { appLegalLinks } from "./shared/legal/LegalLinks";
import { createWebPreferenceStore } from "./shared/preferences/PreferenceStore";
import "./shared/theme/global.css";
import { ownerKey } from "./shared/session/ownerKey";
import { createSignOut, reopenOnSignIn } from "./shared/session/signOut";
import { createSessionTenantSelection } from "./shared/session/tenantSelection";
import {
  localStorageOf,
  storageMarker,
  terminalStartPath,
} from "./features/terminal/terminalMarker";
import { storagePlatformAccessMarker } from "./features/platform/platformAccess";

/*
 * Composition root: the only place where concrete implementations are chosen and browser globals
 * are wired into the application (spec 5.3, 12.1).
 */

const rootElement = document.getElementById("root");

if (rootElement === null) {
  throw new Error("Root element '#root' was not found in index.html.");
}

const config = readAppConfig(import.meta.env);
// The only place that asks Capacitor for the platform (spec 12.1).
const capacitorPlatform = Capacitor.getPlatform();
const platform: PlatformServices =
  capacitorPlatform === "ios" || capacitorPlatform === "android"
    ? new NativePlatformServices({
        name: capacitorPlatform,
        app: CapacitorApp,
        network: Network,
        notifications: PushNotifications,
        badge: capacitorPlatform === "ios" ? registerPlugin<AppBadgePluginPort>("AppBadge") : null,
        geolocation: Geolocation,
        // T-174, ADR-0147: registered by name, so the web bundle carries no NFC code.
        nfc: registerPlugin<NfcPluginPort>("CapacitorNfc"),
        // T-188, ADR-0167: the app's own plugin in the native projects.
        geofence: registerPlugin<GeofenceReminderPluginPort>("GeofenceReminder"),
      })
    : new WebPlatformServices({ window, document, navigator });
if (platform.isNative && config.apiBaseUrl === "") {
  // The apps are served from their own origin (https://localhost, capacitor://localhost).
  throw new Error("VITE_API_BASE_URL must be an absolute URL for the native apps.");
}

// Native apps log in through the system browser, never in the WebView (spec 9.1, ADR-0035).
const userManager = platform.isNative
  ? createNativeUserManager(config.oidc, Browser, Preferences)
  : createBrowserUserManager(config.oidc, window.location.origin);
// One app for all customers asks for the work e-mail address first (T-117, MT-15).
const auth = new OidcAuthService(
  userManager,
  (): number => Math.floor(Date.now() / 1000),
  platform.isNative,
);
// The tenant chosen by people with several employers applies per session (T-105).
const tenants = createSessionTenantSelection(() => window.sessionStorage);
// Whether the signed-in person is operator staff, remembered for the next start (F-69).
const platformAccess = storagePlatformAccessMarker(localStorageOf(window));
const selectedTenant = async (): Promise<string | null> => {
  const user = await auth.getUser();
  return user === null ? null : tenants.get(user.subject);
};
const api = new ApiClient({
  baseUrl: config.apiBaseUrl,
  getAccessToken: (): Promise<string | null> => auth.getAccessToken(),
  fetch: (input, init): Promise<Response> => window.fetch(input, init),
  getTenantId: selectedTenant,
});
// Errors and notable events go to the operator's log, with technical values only (F-68, ADR-0184).
const eventSource = clockEventSource();
const clientLog = new ClientLogger({
  platform:
    eventSource === "Pwa" || eventSource === "Ios" || eventSource === "Android"
      ? eventSource
      : "Web",
  appVersion: config.appVersion,
  deliver: (batch): Promise<ClientLogDelivery> =>
    deliverClientLogs(api, async (): Promise<boolean> => (await auth.getUser()) !== null, batch),
  currentPath: (): string => window.location.pathname,
  schedule: (callback, delayMs): (() => void) => {
    const handle = window.setTimeout(callback, delayMs);
    const cancel = (): void => {
      window.clearTimeout(handle);
    };
    return cancel;
  },
});
reportGlobalErrors(clientLog, window);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    void clientLog.flush();
  }
});
clientLog.info("AppStarted");
/**
 * Reports a failed query or mutation the API, the network or the sign-in does not explain: a
 * response that does not match its schema (with the API path as route) or an error of the code.
 *
 * @param error - The error after the last retry.
 */
function reportQueryError(error: Error): void {
  if (error instanceof ResponseValidationError) {
    clientLog.error("ResponseInvalid", { error, path: error.path.replace(/^\/api\/v\d+/u, "") });
  } else if (
    !(error instanceof ApiError) &&
    !(error instanceof NetworkError) &&
    !(error instanceof AuthenticationRequiredError)
  ) {
    clientLog.error("QueryFailed", { error });
  }
}
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportQueryError }),
  mutationCache: new MutationCache({ onError: reportQueryError }),
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: Error): boolean => {
        const permanent = error instanceof ApiError || error instanceof AuthenticationRequiredError;
        const shouldRetry = !permanent && failureCount < 2;

        return shouldRetry;
      },
      refetchOnWindowFocus: true,
    },
  },
});
const nowMs = (): number => Date.now();
const store = new DexieLocalStore(new TimeTrackingDatabase("timetracking"), nowMs);
const ids = createUuidV7Generator(nowMs, (bytes) => {
  crypto.getRandomValues(bytes);
});
const sync = new SyncEngine({
  store,
  api: createHttpSyncApi(api),
  currentOwner: async (): Promise<string | null> => {
    const user = await auth.getUser();
    // Operator staff record no time: nothing of the clock is synchronized for them (F-69).
    const isOperatorStaff =
      user !== null && (platformAccess.read(user.subject) ?? "none") !== "none";
    const owner =
      user === null || isOperatorStaff ? null : ownerKey(user.subject, tenants.get(user.subject));

    return owner;
  },
  nowMs,
  scheduler: {
    schedule: (callback, delayMs): (() => void) => {
      const handle = window.setTimeout(callback, delayMs);
      const cancel = (): void => {
        window.clearTimeout(handle);
      };
      return cancel;
    },
  },
  random: Math.random,
  timeZone: (): string => Intl.DateTimeFormat().resolvedOptions().timeZone,
  onSynced: (): void => {
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  },
});
// A failed synchronization is reported once per error code; offline and signed out are no failures.
let reportedSyncError: string | null = null;
sync.subscribe((status) => {
  const code = status.phase === "error" ? (status.lastErrorCode ?? "UNKNOWN") : null;

  if (code !== null && code !== reportedSyncError) {
    clientLog.warn("SyncFailed", { errorCode: code });
  }

  reportedSyncError = code;
});
const syncController: SyncController = {
  syncNow: (): Promise<void> => sync.syncNow(),
  getStatus: () => sync.getStatus(),
  subscribe: (listener) => sync.subscribe(listener),
};
// The service worker exists only in the production build (vite-plugin-pwa, ADR-0032). The native
// apps are updated through the stores and bundle their assets, so they run without it (ADR-0035).
const updater =
  import.meta.env.PROD && !platform.isNative && "serviceWorker" in navigator
    ? new ServiceWorkerUpdater({
        host: navigator.serviceWorker,
        scriptUrl: serviceWorkerScriptUrl,
        sync: syncController,
        reload: (): void => {
          window.location.reload();
        },
      })
    : null;
const standalone =
  platform.isNative ||
  window.matchMedia("(display-mode: standalone)").matches ||
  ("standalone" in navigator && navigator.standalone === true);
const platformFacts = {
  userAgent: navigator.userAgent,
  maxTouchPoints: navigator.maxTouchPoints,
  standalone,
};
// Created before rendering so that the early "beforeinstallprompt" event is not missed.
const install = new BrowserInstallController(window, detectPlatform(platformFacts), standalone);
/**
 * Chooses the push notifications of this device: FCM/APNs in the native apps (T-081), Web Push
 * through the service worker in the PWA (T-071).
 *
 * @returns The controller.
 */
function createPushController(): PushNotificationController {
  if (platform.isNative) {
    const native = new NativePushNotificationController({
      channel: platform.name === "ios" ? "Apns" : "Fcm",
      configured: config.nativePush,
      plugin: PushNotifications,
      api: createHttpNativePushApi(api),
      tokens: {
        read: async (): Promise<string | null> => {
          const token = await store.getSetting(settingKeys.nativePushToken);
          return token === "" ? null : token;
        },
        write: (token): Promise<void> => store.putSetting(settingKeys.nativePushToken, token ?? ""),
      },
      schedule: (callback, delayMs): (() => void) => {
        const handle = window.setTimeout(callback, delayMs);
        const cancel = (): void => {
          window.clearTimeout(handle);
        };
        return cancel;
      },
    });
    return native;
  }

  const web =
    updater === null
      ? createUnsupportedPushController()
      : new WebPushNotificationController(
          {
            supported: "PushManager" in window && "Notification" in window,
            requiresInstallation: pushRequiresInstallation(platformFacts),
            permission: (): NotificationPermission => Notification.permission,
            requestPermission: (): Promise<NotificationPermission> =>
              Notification.requestPermission(),
            pushManager: async (): Promise<PushManager> =>
              (await navigator.serviceWorker.ready).pushManager,
          },
          createHttpPushApi(api),
        );

  return web;
}

const push = createPushController();
/**
 * Determines the recorded source of clock events (spec 6.2: Web, Pwa, Ios, Android).
 *
 * @returns The event source of this device.
 */
function clockEventSource(): OfflineServices["eventSource"] {
  let source: OfflineServices["eventSource"] = "Web";

  if (platform.name === "ios") {
    source = "Ios";
  } else if (platform.name === "android") {
    source = "Android";
  } else if (window.matchMedia("(display-mode: standalone)").matches) {
    source = "Pwa";
  }

  return source;
}

// Local personal data is removed before the session ends (spec 11.4); the store is closed first (F-57).
const signOutFlow = createSignOut({
  auth,
  store,
  queryClient,
  releaseDevice: async (): Promise<void> => {
    // The device stops receiving reminders of this account (data minimization).
    await push.removeBeforeSignOut();
    await platform.setBadge(0);
    await platform.clearDeliveredReminders();
    // No region, alarm or reminder of this account stays on the device (T-188).
    await platform.geofence?.clear().catch(() => undefined);
  },
  forgetTenant: (subject) => {
    tenants.clear(subject);
  },
  forgetPlatformAccess: () => {
    platformAccess.forget();
  },
});

const services: AppServices = {
  auth,
  api,
  queryClient,
  preferences: createWebPreferenceStore(() => window.localStorage),
  session: { signOut: signOutFlow.signOut },
  offline: {
    store,
    ids,
    nowMs,
    eventSource,
    deviceTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
  sync: syncController,
  updates: updater ?? createInactiveUpdateController(),
  push,
  install,
  platform,
  tenants,
  // The native apps link the app variants of the legal pages; the browser shows none (ADR-0087).
  legalLinks: appLegalLinks(platform, config.websiteUrl),
  log: clientLog,
  platformAccess,
};

startSyncTriggers(sync, platform, {
  setInterval: (callback, intervalMs): number => window.setInterval(callback, intervalMs),
  clearInterval: (handle): void => {
    window.clearInterval(handle);
  },
});

if (platform.isNative) {
  // The login and logout responses return through the app URI scheme: the in-app browser tab is
  // closed (iOS; Android closes Custom Tabs itself and rejects the call).
  platform.onAppUrlOpen(() => {
    Browser.close().catch(() => undefined);
  });
}

if (updater !== null) {
  const serviceWorker = navigator.serviceWorker;
  startBackgroundSyncSupport({
    sync,
    registrar: async (): Promise<SyncRegistrar | null> => {
      const registration: ServiceWorkerRegistration & { readonly sync?: SyncRegistrar } =
        await serviceWorker.ready;
      return registration.sync ?? null;
    },
    mirrorCredential: async (): Promise<void> => {
      // Only the short-lived access token is shared with the service worker (ADR-0034).
      const user = await auth.getUser();
      const token = await auth.getAccessToken();
      const expiresAtMs = token === null ? null : tokenExpiryMs(token);

      if (user !== null && token !== null && expiresAtMs !== null) {
        const credential = {
          subject: user.subject,
          tenantId: tenants.get(user.subject),
          accessToken: token,
          expiresAtMs,
        };
        await store.putSetting(settingKeys.backgroundSyncCredential, JSON.stringify(credential));
      }
    },
    onRequest: (listener): (() => void) => {
      const onMessage = (event: MessageEvent): void => {
        const source = event.source;

        if (isSyncRequest(event.data) && source instanceof ServiceWorker) {
          listener(event.data, (message) => {
            source.postMessage(message);
          });
        }
      };
      serviceWorker.addEventListener("message", onMessage);
      const stop = (): void => {
        serviceWorker.removeEventListener("message", onMessage);
      };
      return stop;
    },
  });
  updater.start().then(
    () => startUpdateChecks(updater, { window, document }),
    (error: unknown) => {
      // Without a service worker the application still works online; only the type is logged.
      const name = error instanceof Error ? error.name : typeof error;
      console.error(`Service worker registration failed: ${name}`);
      clientLog.warn("ServiceWorkerRegistrationFailed", { error });
    },
  );
}
// A sign-in opens the store again, also after a sign-out in this app instance (F-57).
reopenOnSignIn(auth, store, signOutFlow.isSigningOut, () => {
  void sync.syncNow();
  void push.refresh().catch(() => undefined);
});

// A device paired as terminal opens the terminal screen, never the sign-in of a person (T-173, ADR-0142).
const terminalPath = terminalStartPath(
  window.location.pathname,
  storageMarker(localStorageOf(window)),
);

if (terminalPath !== null) {
  window.history.replaceState(null, "", terminalPath);
}

createRoot(rootElement).render(
  <StrictMode>
    <App services={services} />
  </StrictMode>,
);
